import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Send, MessageCircle, ChevronRight, Image as ImageIcon, X } from 'lucide-react';
import { User } from '../types';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from '../apiEnv';

const MESSAGE_ACTIONS: Record<string, string> = {
  '/messages/conversations': 'message_get_conversations',
  '/messages/thread': 'message_get_thread',
  '/messages/unread': 'message_get_unread',
  '/messages/send': 'message_send',
};

const apiUrl = (path: string, params?: Record<string, string>) => {
  if (isLocalBackend()) {
    const url = new URL(`/api${path}`, window.location.href);
    if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    return url.toString();
  }
  const query = params ? new URLSearchParams(params).toString() : '';
  return phpApiUrl(MESSAGE_ACTIONS[path] || '', query);
};

const requestJson = async (url: string, options?: RequestInit) => {
  let res: Response;
  try {
    res = await fetch(url, {
      cache: 'no-store',
      ...options,
      headers: phpAuthHeaders((options?.headers as Record<string, string>) || undefined),
    });
  } catch {
    throw new Error('网络错误，请稍后重试');
  }
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error('服务器返回数据异常，请稍后重试');
  }
  if (!res.ok) {
    throw new Error(data?.error || `请求失败 (${res.status})`);
  }
  return data;
};

interface Conversation {
  uid: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  isAdmin?: boolean;
  lastMessage: string;
  lastMessageAt: number;
  unread: number;
}

interface ChatMessage {
  id: string;
  from: string;
  to: string;
  text: string;
  imageUrl?: string;
  createdAt: number;
  read: boolean;
}

const MAX_IMAGE_EDGE = 1280;

const fileToCompressedDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('读取图片失败'));
    reader.onload = () => {
      const original = reader.result as string;
      const img = new window.Image();
      img.onerror = () => reject(new Error('图片加载失败'));
      img.onload = () => {
        try {
          const width = img.naturalWidth || img.width;
          const height = img.naturalHeight || img.height;
          const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(width, height, 1));
          if (scale >= 1) {
            resolve(original);
            return;
          }
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(width * scale));
          canvas.height = Math.max(1, Math.round(height * scale));
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(original);
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch {
          resolve(original);
        }
      };
      img.src = original;
    };
    reader.readAsDataURL(file);
  });
};

const formatMessageTime = (ts: number) => {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (sameDay) return time;
  if (date.getFullYear() === now.getFullYear()) return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
};

const Avatar: React.FC<{ displayName: string; avatarUrl?: string; size?: number }> = ({ displayName, avatarUrl, size = 44 }) => {
  const initial = (displayName || '?').trim().charAt(0).toUpperCase();
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={displayName}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-cyan-500/70 to-blue-600/70 text-white flex items-center justify-center font-orbitron font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initial}
    </div>
  );
};

interface MessagesProps {
  user: User;
  onBack: () => void;
  initialWith?: string;
}

const Messages: React.FC<MessagesProps> = ({ user, onBack, initialWith }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeOther, setActiveOther] = useState<Conversation | null>(null);
  const [thread, setThread] = useState<ChatMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [pendingImage, setPendingImage] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [sending, setSending] = useState(false);
  const myUid = String(user.uid);
  const threadScrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchConversations = useCallback(async () => {
    try {
      const data = await requestJson(apiUrl('/messages/conversations'));
      if (data.success) {
        setConversations(Array.isArray(data.conversations) ? data.conversations : []);
      } else {
        setError(data.error || 'Failed to load messages');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchThread = useCallback(async (otherUid: string, silent = false) => {
    if (!silent) setThreadLoading(true);
    setError('');
    try {
      const data = await requestJson(apiUrl('/messages/thread', { with: otherUid }));
      if (data.success) {
        setThread(Array.isArray(data.messages) ? data.messages : []);
        setActiveOther(data.other || { uid: otherUid, email: otherUid, displayName: otherUid, avatarUrl: '', isAdmin: false, lastMessage: '', lastMessageAt: 0, unread: 0 });
      } else {
        setError(data.error || 'Failed to load conversation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      if (!silent) setThreadLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (initialWith) {
      fetchThread(initialWith);
    }
  }, [initialWith, fetchThread]);

  useEffect(() => {
    if (!activeOther) return;
    const timer = window.setInterval(() => {
      fetchThread(activeOther.uid, true);
      fetchConversations();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [activeOther, fetchThread, fetchConversations]);

  useEffect(() => {
    if (threadScrollRef.current) {
      threadScrollRef.current.scrollTop = threadScrollRef.current.scrollHeight;
    }
  }, [thread, activeOther]);

  const openConversation = (conv: Conversation) => {
    fetchThread(conv.uid);
  };

  const backToList = () => {
    setActiveOther(null);
    setThread([]);
    setDraft('');
    setPendingImage('');
  };

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('图片不能超过 15MB');
      return;
    }
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      setPendingImage(dataUrl);
    } catch (err: any) {
      setError(err.message || '图片处理失败');
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && !pendingImage) || !activeOther || sending) return;
    setSending(true);
    try {
      const data = await requestJson(apiUrl('/messages/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: activeOther.uid, text, imageUrl: pendingImage })
      });
      if (data.success) {
        setDraft('');
        setPendingImage('');
        await fetchThread(activeOther.uid);
        fetchConversations();
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-full w-full flex flex-col relative overflow-hidden bg-zinc-950">
      <div className="absolute inset-0 bg-cyan-900/5 pointer-events-none"></div>

      {!activeOther ? (
        <>
          <div className="flex items-center gap-2 md:gap-3 shrink-0 border-b border-white/10 px-3 md:px-5 py-3 md:py-4 bg-black/30 backdrop-blur">
            <button onClick={onBack} className="p-1.5 text-zinc-400 hover:text-cyan-400 transition-colors rounded-lg hover:bg-white/5 shrink-0">
              <ArrowLeft size={22} />
            </button>
            <h1 className="text-lg md:text-2xl font-orbitron font-bold text-white tracking-wider flex items-center gap-2">
              <MessageCircle className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" />
              消息
            </h1>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
            {loading ? (
              <div className="text-center text-zinc-500 py-16 font-orbitron animate-pulse">加载中...</div>
            ) : error ? (
              <div className="text-center text-red-400 py-16 font-orbitron">{error}</div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-zinc-600">
                <MessageCircle className="w-12 h-12 mb-3 opacity-40" />
                <p className="font-orbitron text-sm">暂无消息</p>
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {conversations.map((conv) => (
                  <li key={conv.uid}>
                    <button
                      type="button"
                      onClick={() => openConversation(conv)}
                      className="w-full flex items-center gap-3 px-3 md:px-5 py-3 md:py-4 text-left hover:bg-white/5 transition-colors active:bg-white/[0.06]"
                    >
                      <div className="relative shrink-0">
                        <Avatar displayName={conv.displayName} avatarUrl={conv.avatarUrl} />
                        {conv.isAdmin && (
                          <span className="absolute -bottom-0.5 -right-0.5 rounded bg-cyan-500 text-black text-[8px] font-bold px-1 leading-tight">AD</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-pingfang text-sm md:text-base text-white truncate">{conv.displayName}</span>
                          <span className="text-[11px] text-zinc-500 shrink-0">{formatMessageTime(conv.lastMessageAt)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <span className="text-xs md:text-sm text-zinc-500 truncate">{conv.lastMessage}</span>
                          {conv.unread > 0 && (
                            <span className="shrink-0 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                              {conv.unread > 99 ? '99+' : conv.unread}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-zinc-600 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 md:gap-3 shrink-0 border-b border-white/10 px-3 md:px-5 py-3 md:py-4 bg-black/30 backdrop-blur">
            <button onClick={backToList} className="p-1.5 text-zinc-400 hover:text-cyan-400 transition-colors rounded-lg hover:bg-white/5 shrink-0">
              <ArrowLeft size={22} />
            </button>
            <Avatar displayName={activeOther.displayName} avatarUrl={activeOther.avatarUrl} size={34} />
            <div className="min-w-0">
              <div className="font-pingfang text-sm md:text-base text-white truncate leading-tight">{activeOther.displayName}</div>
              <div className="text-[11px] text-zinc-500 truncate">{activeOther.email}</div>
            </div>
          </div>

          <div ref={threadScrollRef} className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar px-3 md:px-5 py-4 space-y-3">
            {threadLoading ? (
              <div className="text-center text-zinc-500 py-10 font-orbitron animate-pulse">加载中...</div>
            ) : error ? (
              <div className="text-center text-red-400 py-10 font-orbitron">{error}</div>
            ) : thread.length === 0 ? (
              <div className="text-center text-zinc-600 py-10 font-orbitron text-sm">还没有消息，发一条吧</div>
            ) : (
              thread.map((msg) => {
                const mine = String(msg.from) === myUid;
                const hasImage = !!msg.imageUrl;
                return (
                  <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[78%] md:max-w-[60%] overflow-hidden ${hasImage ? 'p-1' : 'px-3 py-2'} rounded-2xl text-sm md:text-base leading-relaxed break-words ${
                      mine
                        ? 'bg-cyan-500 text-black rounded-br-sm'
                        : 'bg-white/10 text-white rounded-bl-sm'
                    }`}>
                      {hasImage && (
                        <button type="button" onClick={() => setPreviewImage(msg.imageUrl || '')} className="block w-full">
                          <img src={msg.imageUrl} alt="消息图片" className="w-full max-w-[240px] md:max-w-[320px] rounded-xl object-contain" />
                        </button>
                      )}
                      {msg.text ? <div className={hasImage ? 'mt-1 px-1.5 pb-0.5' : ''}>{msg.text}</div> : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="shrink-0 border-t border-white/10 bg-black/40 backdrop-blur px-3 md:px-5 py-3">
            {pendingImage && (
              <div className="relative inline-block mb-2">
                <img src={pendingImage} alt="待发送图片" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                <button
                  type="button"
                  onClick={() => setPendingImage('')}
                  className="absolute -top-1.5 -right-1.5 rounded-full bg-black/80 border border-white/20 text-white p-0.5"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              <button
                type="button"
                onClick={handlePickImage}
                className="shrink-0 rounded-xl p-2.5 text-zinc-400 hover:text-cyan-400 hover:bg-white/5 transition-colors"
              >
                <ImageIcon size={20} />
              </button>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                rows={1}
                placeholder="输入消息..."
                className="flex-1 resize-none rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm md:text-base text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/50"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={(!draft.trim() && !pendingImage) || sending}
                className={`shrink-0 rounded-xl p-2.5 transition-colors ${
                  (draft.trim() || pendingImage) && !sending ? 'bg-cyan-500 text-black hover:bg-cyan-400' : 'bg-white/5 text-zinc-600'
                }`}
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </>
      )}

      {previewImage && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
          onClick={() => setPreviewImage('')}
        >
          <img src={previewImage} alt="图片预览" className="max-w-full max-h-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
};

export default Messages;
